// components/StudentPortal/Exam/StudentExam.js
// Examen v2 (S3, F6 frontend). 3 fases: intro (con historial de intentos) ->
// preguntas (timer + botones forma+color, SIN respuesta correcta del servidor)
// -> resultado (score + intentos + mejor marca). Repetible.
// Backend F6: /quizzes/play (sin correct), submitStudentQuiz (server corrige +
// dual-write QuizAttempt), /quizzes/attempts (historial), markFinalQuizDone.
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../hooks";
import { QuizApi } from "../../../api/quiz";
import { TeacherTasksAPI } from "../../../api/teacherTasksAPI";
import { EXAM_ANSWERS, SECONDS_PER_QUESTION } from "./examColors";
import styles from "./StudentExam.module.scss";

const quizApi = new QuizApi();
const tasksApi = new TeacherTasksAPI();

export function StudentExam({ quizId, taskId, onClose, onGoHome, mode }) {
  const { t } = useTranslation("students");
  const { user, accessToken } = useAuth();
  const studentId = user?.user?.student;
  // "Ir a inicio" lleva al home del alumno (no a la pantalla intermedia de la
  // tarea). Si no se pasa, cae a onClose.
  const goHome = typeof onGoHome === "function" ? onGoHome : onClose;
  // VR: superficies oscuras y targets aun mas grandes.
  const vrCls = mode === "vr" ? styles.vr : "";

  const [phase, setPhase] = useState("loading"); // loading|intro|playing|submitting|result|error
  const [quiz, setQuiz] = useState(null);
  const [attempts, setAttempts] = useState({ list: [], best: null, count: 0 });
  const [qIndex, setQIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(SECONDS_PER_QUESTION);
  const [lastResult, setLastResult] = useState(null);

  const answersRef = useRef([]);
  const qIndexRef = useRef(0);
  const lockRef = useRef(false);
  const startRef = useRef(null);
  const timerRef = useRef(null);
  const handlerRef = useRef(() => {});
  qIndexRef.current = qIndex;

  const questions = quiz?.questions || [];
  const total = questions.length;
  const current = questions[qIndex];

  const loadAttempts = useCallback(async () => {
    try {
      const a = await quizApi.getStudentQuizAttempts(
        accessToken,
        studentId,
        quizId,
      );
      setAttempts({
        list: a.attempts || [],
        best: a.best || null,
        count: a.attemptsCount || 0,
      });
    } catch (_e) {
      /* historial opcional */
    }
  }, [accessToken, studentId, quizId]);

  // Carga inicial (intro)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const p = await quizApi.getQuizForPlay(accessToken, quizId);
        if (!alive) return;
        setQuiz(p.quiz);
        await loadAttempts();
        if (alive) setPhase("intro");
      } catch (_e) {
        if (alive) setPhase("error");
      }
    })();
    return () => {
      alive = false;
    };
  }, [accessToken, quizId, loadAttempts]);

  const submitExam = useCallback(
    async (finalAnswers) => {
      setPhase("submitting");
      clearInterval(timerRef.current);
      const responses = finalAnswers.map((a) => ({
        questionIndex: a.questionIndex,
        selected: a.selected,
        timeTaken: a.timeTaken,
      }));
      const timeTaken = Math.round(
        responses.reduce((s, r) => s + (r.timeTaken || 0), 0),
      );
      try {
        const saveRes = await quizApi.submitStudentQuiz(
          accessToken,
          studentId,
          {
            teacherQuizId: quizId,
            responses,
            timeTaken,
            totalTime: total * SECONDS_PER_QUESTION,
            taskId: taskId || undefined,
          },
        );
        const sq = saveRes?.studentQuiz || {};
        setLastResult({
          totalCorrect: sq.totalCorrect || 0,
          totalIncorrect: sq.totalIncorrect || 0,
          timeTaken: sq.timeTaken || timeTaken,
          total,
        });
        if (taskId) {
          try {
            await tasksApi.markFinalQuizDone(
              accessToken,
              studentId,
              taskId,
              true,
            );
          } catch (_e) {
            /* el examen ya quedo guardado */
          }
        }
        await loadAttempts();
        setPhase("result");
      } catch (_e) {
        setPhase("error");
      }
    },
    [accessToken, studentId, quizId, taskId, total, loadAttempts],
  );

  // Handler de respuesta (estable via ref; el timer lo invoca sin ser dependencia)
  const handleAnswer = (selected) => {
    if (lockRef.current) return;
    lockRef.current = true;
    clearInterval(timerRef.current);
    const tt = startRef.current ? (Date.now() - startRef.current) / 1000 : 0;
    answersRef.current = [
      ...answersRef.current,
      {
        questionIndex: qIndexRef.current,
        selected: typeof selected === "string" ? selected : "",
        timeTaken: parseFloat(tt.toFixed(2)),
      },
    ];
    setTimeout(() => {
      if (qIndexRef.current + 1 < total) setQIndex(qIndexRef.current + 1);
      else submitExam(answersRef.current);
    }, 350);
  };
  handlerRef.current = handleAnswer;

  // Timer por pregunta (deps minimas -> no se reinicia a mitad)
  useEffect(() => {
    if (phase !== "playing" || !current) return undefined;
    lockRef.current = false;
    startRef.current = Date.now();
    setTimeLeft(SECONDS_PER_QUESTION);
    timerRef.current = setInterval(() => {
      setTimeLeft((tl) => {
        if (tl <= 1) {
          clearInterval(timerRef.current);
          handlerRef.current(""); // timeout = sin respuesta
          return 0;
        }
        return tl - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, qIndex]);

  const startExam = () => {
    answersRef.current = [];
    setQIndex(0);
    setLastResult(null);
    setPhase("playing");
  };

  // -------- Render --------
  if (phase === "loading" || phase === "submitting") {
    return <div className={styles.state}>{t("exam.loading")}</div>;
  }
  if (phase === "error") {
    return (
      <div className={styles.state}>
        {t("exam.error")}{" "}
        <button type="button" className={styles.linkBtn} onClick={onClose}>
          {t("newHome.back")}
        </button>
      </div>
    );
  }

  if (phase === "intro") {
    return (
      <div className={`${styles.intro} ${vrCls}`}>
        <span className={styles.pill}>{t("exam.finalExam")}</span>
        <h1 className={styles.examTitle}>{quiz?.name}</h1>
        <div className={styles.chips}>
          <span>{t("exam.nQuestions", { n: total })}</span>
          <span>{t("exam.perQuestion", { s: SECONDS_PER_QUESTION })}</span>
          <span className={styles.repeat}>{t("exam.repeatable")}</span>
        </div>
        {attempts.count > 0 && (
          <div className={styles.attempts}>
            <h3>{t("exam.yourAttempts")}</h3>
            <div className={styles.attemptRow}>
              {attempts.list.map((a) => (
                <span
                  key={a.attemptNumber}
                  className={`${styles.attemptChip} ${
                    attempts.best &&
                    a.attemptNumber === attempts.best.attemptNumber
                      ? styles.bestChip
                      : ""
                  }`}
                >
                  {t("exam.attemptN", { n: a.attemptNumber })}: {a.totalCorrect}
                  /{a.total} · {Math.round(a.timeTaken)}s
                </span>
              ))}
            </div>
          </div>
        )}
        <button type="button" className={styles.cta} onClick={startExam}>
          {attempts.count > 0 ? t("exam.retry") : t("exam.start")}
        </button>
        <button type="button" className={styles.linkBtn} onClick={onClose}>
          {t("exam.backToTask")}
        </button>
      </div>
    );
  }

  if (phase === "playing" && current) {
    return (
      <div className={`${styles.play} ${vrCls}`}>
        <div className={styles.playTop}>
          <span className={styles.progressLabel}>
            {t("exam.questionOf", { i: qIndex + 1, n: total })}
          </span>
          <div className={styles.progressBar}>
            <span style={{ width: `${((qIndex + 1) / total) * 100}%` }} />
          </div>
        </div>
        <div className={styles.timer}>{timeLeft}</div>
        <h2 className={styles.question}>{current.question}</h2>
        <div className={styles.answers}>
          {(current.responses || []).map((resp, i) => {
            const cfg = EXAM_ANSWERS[i % EXAM_ANSWERS.length];
            return (
              <button
                key={i}
                type="button"
                className={styles.answerBtn}
                style={{
                  background: cfg.color,
                  color: cfg.dark ? "#1f2430" : "#fff",
                }}
                onClick={() => handlerRef.current(resp)}
              >
                <span className={styles.shape}>{cfg.shape}</span>
                {resp}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (phase === "result" && lastResult) {
    const passed = lastResult.totalCorrect >= Math.ceil(lastResult.total * 0.7);
    return (
      <div className={`${styles.result} ${vrCls}`}>
        <h1 className={styles.resultTitle}>
          {passed ? t("exam.greatJob") : t("exam.keepGoing")}
        </h1>
        <div className={styles.score}>
          <strong>
            {lastResult.totalCorrect}/{lastResult.total}
          </strong>
          <span>{t("exam.correct")}</span>
        </div>
        <div className={styles.chips}>
          <span>
            {t("exam.time")}: {Math.round(lastResult.timeTaken)}s
          </span>
          {attempts.best && (
            <span className={styles.repeat}>
              {t("exam.best")}: {attempts.best.totalCorrect}/
              {attempts.best.total}
            </span>
          )}
        </div>
        <div className={styles.resultActions}>
          <button type="button" className={styles.ghost} onClick={startExam}>
            {t("exam.retry")}
          </button>
          <button type="button" className={styles.cta} onClick={goHome}>
            {t("exam.goHome")}
          </button>
        </div>
      </div>
    );
  }

  return null;
}
